/**
 * Click-and-drag scroll with velocity/momentum.
 * Attach to a scrollable container element.
 *
 * @param {Object} options
 * @param {Function} options.isInteractive - (event) => bool - return true to skip drag-scroll
 * @returns {{ attach(el: HTMLElement): void, detach(): void }}
 */
export function useDragScroll(options = {}) {
  const { isInteractive } = options;

  const THRESHOLD = 5;       // px movement before drag begins
  const FRICTION = 0.92;     // velocity decay per frame
  const MIN_VELOCITY = 0.5;  // px/frame to stop momentum

  let el = null;
  let isDragging = false;
  let dragPending = false;
  let startX, startY, scrollStartX, scrollStartY;
  let lastX, lastY, lastTime;
  let velocityX = 0, velocityY = 0;
  let momentumId = null;

  function onPointerDown(e) {
    if (e.button !== 0) return;
    if (isInteractive && isInteractive(e)) return;

    dragPending = true;
    startX = e.clientX;
    startY = e.clientY;
    scrollStartX = el.scrollLeft;
    scrollStartY = el.scrollTop;
    lastX = e.clientX;
    lastY = e.clientY;
    lastTime = performance.now();
    velocityX = 0;
    velocityY = 0;
    stopMomentum();

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }

  function onPointerMove(e) {
    if (!dragPending && !isDragging) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (dragPending) {
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
      isDragging = true;
      dragPending = false;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    }

    e.preventDefault();

    el.scrollLeft = scrollStartX - dx;
    el.scrollTop = scrollStartY - dy;

    // Exponential smoothing for velocity (px/ms)
    const now = performance.now();
    const dt = now - lastTime;
    if (dt > 0) {
      const alpha = 0.3;
      velocityX = alpha * ((e.clientX - lastX) / dt) + (1 - alpha) * velocityX;
      velocityY = alpha * ((e.clientY - lastY) / dt) + (1 - alpha) * velocityY;
    }
    lastX = e.clientX;
    lastY = e.clientY;
    lastTime = now;
  }

  function onPointerUp() {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);

    const wasDragging = isDragging;
    isDragging = false;
    dragPending = false;

    if (wasDragging) {
      el.style.cursor = '';
      el.style.userSelect = '';
      applyMomentum();
    }
  }

  function applyMomentum() {
    // Convert from px/ms to px/frame (~16ms)
    let vx = velocityX * 16;
    let vy = velocityY * 16;

    function step() {
      vx *= FRICTION;
      vy *= FRICTION;

      if (Math.abs(vx) < MIN_VELOCITY && Math.abs(vy) < MIN_VELOCITY) {
        momentumId = null;
        return;
      }

      el.scrollLeft -= vx;
      el.scrollTop -= vy;
      momentumId = requestAnimationFrame(step);
    }

    if (Math.abs(vx) > MIN_VELOCITY || Math.abs(vy) > MIN_VELOCITY) {
      momentumId = requestAnimationFrame(step);
    }
  }

  function stopMomentum() {
    if (momentumId) {
      cancelAnimationFrame(momentumId);
      momentumId = null;
    }
  }

  function attach(scrollEl) {
    el = scrollEl;
    el.style.cursor = 'grab';
    el.addEventListener('pointerdown', onPointerDown);
  }

  function detach() {
    if (!el) return;
    el.style.cursor = '';
    el.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    stopMomentum();
    el = null;
  }

  return { attach, detach };
}
